import React, { useEffect, useMemo, useState } from 'react';
import usePageTranslation from '../../../../shared/hooks/usePageTranslation';
import { Batiment, Personnel, Vehicule } from '../../../../shared/types/game';
import { getApiBaseUrl } from '../../../../core/config/api';

interface BuildingDetailsCTATabProps {
  ctaBuilding: Batiment;
}

const TYPE_ORDER = ['pompier', 'hopital', 'fdo', 'prison'] as const;

function sortTypes(a: string, b: string): number {
  const ia = TYPE_ORDER.indexOf(a as (typeof TYPE_ORDER)[number]);
  const ib = TYPE_ORDER.indexOf(b as (typeof TYPE_ORDER)[number]);
  if (ia === -1 && ib === -1) return a.localeCompare(b, 'fr');
  if (ia === -1) return 1;
  if (ib === -1) return -1;
  return ia - ib;
}

const BuildingDetailsCTATab: React.FC<BuildingDetailsCTATabProps> = ({ ctaBuilding }) => {
  const { t } = usePageTranslation('game', 'Map');
  const [allBuildings, setAllBuildings] = useState<Batiment[]>([]);
  const [allPersonnel, setAllPersonnel] = useState<Personnel[]>([]);
  const [allVehicules, setAllVehicules] = useState<Vehicule[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const load = async () => {
      setLoading(true);
      try {
        const [bRes, pRes, vRes] = await Promise.all([
          fetch(`${getApiBaseUrl(true)}/buildings`, {
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          }),
          fetch(`${getApiBaseUrl(true)}/buildings/personnel`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${getApiBaseUrl(true)}/buildings/vehicules`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        if (bRes.ok) {
          const d = await bRes.json();
          if (d.success && Array.isArray(d.data)) setAllBuildings(d.data);
        }
        if (pRes.ok) {
          const d = await pRes.json();
          if (d.success && Array.isArray(d.data)) setAllPersonnel(d.data);
        }
        if (vRes.ok) {
          const d = await vRes.json();
          if (d.success && Array.isArray(d.data)) setAllVehicules(d.data);
        }
      } catch {
        /* ignore */
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [ctaBuilding.id]);

  const linkedBuildings = useMemo(
    () =>
      allBuildings.filter(
        (b) =>
          b.actif &&
          b.type !== 'cta' &&
          b.cta_id != null &&
          Number(b.cta_id) === Number(ctaBuilding.id)
      ),
    [allBuildings, ctaBuilding.id]
  );

  const rowsByType = useMemo(() => {
    const byType = new Map<string, Batiment[]>();
    for (const b of linkedBuildings) {
      const typ = (b.type && String(b.type).trim()) || 'autre';
      if (!byType.has(typ)) byType.set(typ, []);
      byType.get(typ)!.push(b);
    }
    const rows: { type: string; buildings: number; personnel: number; vehicules: number }[] = [];
    Array.from(byType.entries()).forEach(([type, blist]) => {
      const buildingIds = new Set(blist.map((b) => Number(b.id)));
      const personnel = allPersonnel.filter(
        (p) => p.batiment_id != null && buildingIds.has(Number(p.batiment_id))
      ).length;
      const vehicules = allVehicules.filter(
        (v) => v.batiment_id != null && buildingIds.has(Number(v.batiment_id))
      ).length;
      rows.push({ type, buildings: blist.length, personnel, vehicules });
    });
    rows.sort((x, y) => sortTypes(x.type, y.type));
    return rows;
  }, [linkedBuildings, allPersonnel, allVehicules]);

  const totals = useMemo(() => {
    const buildingIds = new Set(linkedBuildings.map((b) => Number(b.id)));
    const personnel = allPersonnel.filter(
      (p) => p.batiment_id != null && buildingIds.has(Number(p.batiment_id))
    ).length;
    const vehicules = allVehicules.filter(
      (v) => v.batiment_id != null && buildingIds.has(Number(v.batiment_id))
    ).length;
    return {
      types: rowsByType.length,
      buildings: linkedBuildings.length,
      personnel,
      vehicules,
    };
  }, [linkedBuildings, allPersonnel, allVehicules, rowsByType.length]);

  const typeLabel = (type: string) => {
    const key = `contextMenu.buildingTypes.${type}`;
    const label = t(key);
    return label === key ? type : label;
  };

  const linkedSorted = useMemo(
    () =>
      [...linkedBuildings].sort((a, b) =>
        a.nom.localeCompare(b.nom, 'fr', { sensitivity: 'base' })
      ),
    [linkedBuildings]
  );

  if (loading) {
    return (
      <div className="flex justify-center py-12 text-slate-500">{t('ctaDetails.loading')}</div>
    );
  }

  const kpiRow = (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      <div className="rounded-xl border border-slate-200 bg-white px-3 py-3 text-center shadow-sm">
        <div className="text-2xl font-bold tabular-nums text-slate-900">{totals.types}</div>
        <div className="text-xs font-medium uppercase tracking-wide text-slate-500">{t('ctaDetails.kpiTypes')}</div>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white px-3 py-3 text-center shadow-sm">
        <div className="text-2xl font-bold tabular-nums text-slate-900">{totals.buildings}</div>
        <div className="text-xs font-medium uppercase tracking-wide text-slate-500">{t('ctaDetails.kpiSites')}</div>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white px-3 py-3 text-center shadow-sm">
        <div className="text-2xl font-bold tabular-nums text-slate-900">{totals.personnel}</div>
        <div className="text-xs font-medium uppercase tracking-wide text-slate-500">{t('ctaDetails.kpiPersonnel')}</div>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white px-3 py-3 text-center shadow-sm">
        <div className="text-2xl font-bold tabular-nums text-slate-900">{totals.vehicules}</div>
        <div className="text-xs font-medium uppercase tracking-wide text-slate-500">{t('ctaDetails.kpiVehicules')}</div>
      </div>
    </div>
  );

  const tableBlock =
    linkedBuildings.length === 0 ? (
      <p className="rounded-lg border border-dashed border-slate-300 bg-white py-10 text-center text-sm text-slate-500">
        {t('ctaDetails.empty')}
      </p>
    ) : (
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50/90">
              <th className="px-4 py-3 font-bold uppercase tracking-wide text-blue-950">{t('ctaDetails.colType')}</th>
              <th className="px-4 py-3 text-right font-bold uppercase tracking-wide text-blue-950">
                {t('ctaDetails.colBuildings')}
              </th>
              <th className="px-4 py-3 text-right font-bold uppercase tracking-wide text-blue-950">
                {t('ctaDetails.colPersonnel')}
              </th>
              <th className="px-4 py-3 text-right font-bold uppercase tracking-wide text-blue-950">
                {t('ctaDetails.colVehicules')}
              </th>
            </tr>
          </thead>
          <tbody>
            {rowsByType.map((row) => (
              <tr key={row.type} className="border-b border-slate-100 last:border-0">
                <td className="px-4 py-3 font-medium text-slate-900">{typeLabel(row.type)}</td>
                <td className="px-4 py-3 text-right tabular-nums text-slate-800">{row.buildings}</td>
                <td className="px-4 py-3 text-right tabular-nums text-slate-800">{row.personnel}</td>
                <td className="px-4 py-3 text-right tabular-nums text-slate-800">{row.vehicules}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-slate-200 bg-slate-50/80">
              <td className="px-4 py-3 font-bold text-slate-900">{t('ctaDetails.totals')}</td>
              <td className="px-4 py-3 text-right font-bold tabular-nums text-slate-900">{totals.buildings}</td>
              <td className="px-4 py-3 text-right font-bold tabular-nums text-slate-900">{totals.personnel}</td>
              <td className="px-4 py-3 text-right font-bold tabular-nums text-slate-900">{totals.vehicules}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    );

  const leftPane = (
    <aside className="lg:col-span-3">
      <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
        <h4 className="mb-2 border-b border-slate-200 pb-2 text-xs font-bold uppercase tracking-wide text-blue-900">
          {t('ctaDetails.leftTitle')}
        </h4>
        {linkedSorted.length === 0 ? (
          <p className="py-6 text-center text-xs text-slate-500">{t('ctaDetails.leftEmpty')}</p>
        ) : (
          <ul className="max-h-[min(22rem,50vh)] space-y-0 overflow-y-auto pr-1">
            {linkedSorted.map((b) => (
              <li key={b.id} className="border-b border-slate-100 py-2.5 last:border-0">
                <div className="text-[0.65rem] font-semibold uppercase tracking-wide text-slate-500">
                  {typeLabel(b.type)}
                </div>
                <div className="text-sm font-medium leading-snug text-slate-900">{b.nom}</div>
                <div className="text-xs text-slate-500">
                  {t('level')} {b.niveau}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </aside>
  );

  const rightPane = (
    <aside className="lg:col-span-3">
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm lg:sticky lg:top-2 lg:self-start">
        <h4 className="border-b border-slate-200 pb-2 text-xs font-bold uppercase tracking-wide text-blue-900">
          {t('ctaDetails.sidebarTitle')}
        </h4>
        <dl className="mt-3 space-y-2.5 text-sm">
          <div className="flex items-baseline justify-between gap-2">
            <dt className="text-slate-600">{t('ctaDetails.kpiTypes')}</dt>
            <dd className="font-bold tabular-nums text-slate-900">{totals.types}</dd>
          </div>
          <div className="flex items-baseline justify-between gap-2">
            <dt className="text-slate-600">{t('ctaDetails.kpiSites')}</dt>
            <dd className="font-bold tabular-nums text-slate-900">{totals.buildings}</dd>
          </div>
          <div className="flex items-baseline justify-between gap-2">
            <dt className="text-slate-600">{t('ctaDetails.kpiPersonnel')}</dt>
            <dd className="font-bold tabular-nums text-slate-900">{totals.personnel}</dd>
          </div>
          <div className="flex items-baseline justify-between gap-2">
            <dt className="text-slate-600">{t('ctaDetails.kpiVehicules')}</dt>
            <dd className="font-bold tabular-nums text-slate-900">{totals.vehicules}</dd>
          </div>
        </dl>
        <p className="mt-4 border-t border-slate-100 pt-3 text-xs leading-relaxed text-slate-500">
          {t('ctaDetails.sidebarHint')}
        </p>
      </div>
    </aside>
  );

  return (
    <div className="rounded-2xl border border-slate-300/70 bg-gradient-to-b from-slate-100 to-slate-200/90 p-4 text-slate-900 shadow-inner sm:p-5">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-12 lg:items-start">
        <div className="lg:col-span-12">
          <h3 className="text-base font-extrabold uppercase tracking-wide text-blue-900">{t('ctaDetails.title')}</h3>
          <p className="mt-1 text-sm text-slate-600">{t('ctaDetails.subtitle')}</p>
        </div>

        {leftPane}

        <div className="space-y-4 lg:col-span-6">
          {kpiRow}
          {tableBlock}
        </div>

        {rightPane}
      </div>
    </div>
  );
};

export default BuildingDetailsCTATab;
